import { useEffect, useRef, useState } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { Button } from '../Button';
import {
  FooterBar,
  FooterDiscountedPrice,
  FooterLeft,
  FooterOriginalPrice,
  FooterPriceRow,
  FooterSpacer,
  FooterTitle,
  FooterTotal,
} from './StickyFooter.styles';
import type { StickyFooterProps } from './StickyFooter.types';

export function StickyFooter({
  title,
  totalLabel = 'Total',
  totalAmount,
  originalAmount,
  currencyCode,
  locale = 'en-GB',
  ctaLabel,
  onCtaClick,
  ctaDisabled,
  align,
  inline = false,
}: StickyFooterProps) {
  const barRef = useRef<HTMLElement>(null);
  const [barHeight, setBarHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const next = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      setBarHeight(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const formattedTotal =
    totalAmount != null ? formatCurrency(totalAmount, currencyCode, { locale }) : null;
  const hasDiscount =
    totalAmount != null && originalAmount != null && originalAmount !== totalAmount;
  const originalAmountFormatted = hasDiscount
    ? formatCurrency(originalAmount, currencyCode, { locale })
    : null;

  return (
    <>
      {!inline && (
        <FooterSpacer aria-hidden style={barHeight != null ? { height: barHeight } : undefined} />
      )}
      <FooterBar ref={barRef} $align={align} data-inline={inline || undefined}>
        <FooterLeft>
          <FooterTitle>{title}</FooterTitle>
          <div aria-live="polite" aria-atomic="true">
            {hasDiscount ? (
              <FooterPriceRow>
                <FooterTotal>{totalLabel}</FooterTotal>
                <FooterDiscountedPrice>{formattedTotal}</FooterDiscountedPrice>
                <FooterOriginalPrice>{originalAmountFormatted}</FooterOriginalPrice>
              </FooterPriceRow>
            ) : (
              formattedTotal && <FooterTotal>{`${totalLabel} ${formattedTotal}`}</FooterTotal>
            )}
          </div>
        </FooterLeft>
        <Button
          type="button"
          variant="primary"
          size="large"
          onClick={onCtaClick}
          disabled={ctaDisabled}
        >
          {ctaLabel}
        </Button>
      </FooterBar>
    </>
  );
}
